import { Button, ResponsiveDialog, ResponsiveDialogHeader, SegmentedToggle, Textarea, toast } from '@parsleyai/ui'
import { Bug, Loader2, Send } from 'lucide-react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { type FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { FeedbackType, gatherFeedbackContext } from '@/lib/feedbackContext'
import { callSubmitFeedback } from '@/services/firebase/functions'
import { PlanGenPhase, usePlanGen, useTheme } from '@/store'
import { FeedbackMark } from './FeedbackMark'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
}

// The feedback & bug-reporting modal. Opened from the always-available header trigger
//. A segmented Feedback | Bug toggle swaps the form in place; the message is the only
// required field. Everything else (route, version, device, plan state) is captured silently
// at submit time and stamped with the caller's identity server-side (spec D2/D4). Built on
// the responsive-dialog primitive — a centered dialog on desktop, a bottom sheet on mobile.
export default function FeedbackDialog({ open, onOpenChange }: Props) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const { phase, draftId } = usePlanGen()
  const location = useLocation()
  const [view, setView] = useState<FeedbackType>(FeedbackType.Feedback)
  const [message, setMessage] = useState('')
  const [expected, setExpected] = useState('')
  const [sending, setSending] = useState(false)
  const reduced = useReducedMotion()
  // The height-morph wrapper clips its overflow while animating; once open, we drop the clip
  // so the field's focus ring isn't cut off at rest.
  const [clipExpected, setClipExpected] = useState(true)

  const isBug = view === FeedbackType.Bug
  const canSend = message.trim().length > 0 && !sending

  const handleOpenChange = (next: boolean) => {
    onOpenChange(next)
    if (!next) {
      // Reset after the close animation finishes so the reset isn't visible.
      setTimeout(() => {
        setView(FeedbackType.Feedback)
        setMessage('')
        setExpected('')
        setSending(false)
      }, 250)
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!canSend) return
    setSending(true)
    try {
      const clientContext = gatherFeedbackContext({
        route: location.pathname + location.search,
        theme: isDark ? 'dark' : 'light',
        planContext: phase !== PlanGenPhase.Idle ? { phase, ...(draftId ? { planId: draftId } : {}) } : undefined
      })
      // Omit `expected` entirely when empty — the Firebase callable encoder serializes an
      // `undefined` field as `null` (unlike JSON.stringify, which drops it), and the callable
      // rejects a non-string `expected`. A conditional spread keeps it off the wire.
      const trimmedExpected = isBug ? expected.trim() : ''
      await callSubmitFeedback({
        type: view,
        message: message.trim(),
        ...(trimmedExpected.length > 0 ? { expected: trimmedExpected } : {}),
        clientContext
      })
      toast.success(t('feedback.toast.success'), { id: 'feedback' })
      handleOpenChange(false)
    } catch (_err) {
      // Keep the modal open with the text preserved so the user can retry.
      toast.error(t('feedback.toast.error'), { id: 'feedback' })
    } finally {
      setSending(false)
    }
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={handleOpenChange} aria-label={t('feedback.heading')}>
      <ResponsiveDialogHeader
        className="mb-5"
        icon={<FeedbackMark className="h-6 w-6" />}
        title={t('feedback.heading')}
        description={t('feedback.description')}
      />

      <SegmentedToggle<FeedbackType>
        aria-label={t('feedback.type.label')}
        value={view}
        onValueChange={setView}
        className="mb-5"
        options={[
          { value: FeedbackType.Feedback, label: t('feedback.type.feedback'), icon: <FeedbackMark strokeWidth={2} /> },
          { value: FeedbackType.Bug, label: t('feedback.type.bug'), icon: <Bug /> }
        ]}
      />

      <form onSubmit={handleSubmit}>
        <div>
          <div>
            <label htmlFor="feedback-message" className="mb-2 block text-sm font-semibold text-foreground">
              {isBug ? t('feedback.message.bugLabel') : t('feedback.message.feedbackLabel')}
            </label>
            <Textarea
              id="feedback-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={isBug ? t('feedback.message.bugPlaceholder') : t('feedback.message.feedbackPlaceholder')}
            />
          </div>
          {/* The optional bug field animates its own height (0 ↔ auto); because the dialog is
              content-height, the modal reflows to follow — a smooth morph, no jarring jump. */}
          <AnimatePresence initial={false}>
            {isBug && (
              <motion.div
                key="expected"
                initial={reduced ? false : { height: 0, opacity: 0, marginBottom: 0 }}
                animate={{ height: 'auto', opacity: 1, marginBottom: -20 }}
                exit={reduced ? { opacity: 0 } : { height: 0, opacity: 0, marginBottom: 0 }}
                transition={{ duration: reduced ? 0 : 0.4, ease: [0.25, 0.1, 0.25, 1] }}
                onAnimationStart={() => setClipExpected(true)}
                onAnimationComplete={() => setClipExpected(false)}
                // Bottom room (pb-5) keeps the textarea's shadow inside the clipped box so it
                // reveals with the field instead of popping in. The offsetting negative margin is
                // *animated* (not a static -mb) so it collapses in step with the height on exit —
                // otherwise the footer over-shrinks and snaps back when the field unmounts.
                className={clipExpected ? 'overflow-hidden' : ''}>
                <div className="pt-3.5 pb-5">
                  <label htmlFor="feedback-expected" className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-foreground">
                    {t('feedback.expected.label')}
                    <span className="text-xs font-medium text-faint">{t('feedback.expected.optional')}</span>
                  </label>
                  <Textarea
                    id="feedback-expected"
                    className="min-h-[84px]"
                    value={expected}
                    onChange={(e) => setExpected(e.target.value)}
                    placeholder={t('feedback.expected.placeholder')}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="mt-5 flex items-center gap-3.5">
          <span className="hidden flex-1 text-xs text-faint md:block">{t('feedback.hint')}</span>
          <Button
            type="submit"
            variant="subtle"
            size="cta"
            disabled={!canSend}
            className="w-full gap-2 px-6 text-base font-semibold md:w-auto md:min-w-[108px]">
            {sending ? (
              <>
                <Loader2 className="animate-spin" />
                {t('feedback.sending')}
              </>
            ) : (
              <>
                {t('feedback.send')}
                <Send />
              </>
            )}
          </Button>
        </div>
      </form>
    </ResponsiveDialog>
  )
}
